import { ReactNode } from 'react';
import classNames from 'classnames';

import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';

import { FormInputSize } from '../types';

import styles from './invalid-reference.module.scss';

export interface InvalidReferenceProps {
  value: string;
  entityName?: string;
  /**
   * `input` fills its container and shows a chevron, for use as a select trigger.
   * `inline` sizes to its content, for use among tags or other inline content.
   */
  variant?: 'input' | 'inline';
  size?: FormInputSize;
  selected?: boolean;
  disabled?: boolean;
  className?: classNames.Argument;
  ref?: React.Ref<HTMLButtonElement>;
  /** When given, renders as a button. Omit for a non-interactive label. */
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  tooltip?: ReactNode;
}

/**
 * Error state for a value referencing an entity that no longer exists.
 *
 * Rendered in place of a value's normal UI so a broken reference reads as a recoverable
 * error rather than a missing or wrong item.
 */
export const InvalidReference = (props: InvalidReferenceProps) => {
  const {
    value,
    entityName = 'Reference',
    variant = 'input',
    size = 'small',
    selected,
    disabled = false,
    className,
    ref,
    onClick,
    onKeyDown,
    tooltip = `${entityName} not found: ${value}`,
  } = props;

  const content = (
    <>
      <Icon name="Alert" size={14} className={styles.icon} />
      <code className={styles.reference}>{value}</code>
      <span className={styles.message}> not found</span>
    </>
  );

  const rootClassName = classNames(styles.invalidReference, className, {
    [styles.input]: variant === 'input',
    [styles.inline]: variant === 'inline',
    [styles.unselected]: selected === false,
    [styles.sizeSmall]: size === 'small',
    [styles.sizeMedium]: size === 'regular',
    [styles.sizeLarge]: size === 'large',
  });

  const element =
    onClick === undefined ? (
      <span className={rootClassName}>{content}</span>
    ) : (
      <button
        type="button"
        className={rootClassName}
        disabled={disabled}
        onClick={onClick}
        onKeyDown={onKeyDown}>
        {content}
        {variant === 'input' && (
          <div className={styles.chevron}>
            <svg
              focusable="false"
              preserveAspectRatio="xMidYMid meet"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              aria-hidden="true">
              <path d="M8 11L3 6 3.7 5.3 8 9.6 12.3 5.3 13 6z" />
            </svg>
          </div>
        )}
      </button>
    );

  return (
    <Tooltip content={tooltip} color="error" ref={ref}>
      {element}
    </Tooltip>
  );
};
